/**
 * Talks to the backend's agency API (https://api.twiva.co.ke/api/v1/cpaas/agency,
 * twiva-server's Http/Controllers/Api/Agency) on behalf of screens written for
 * our API, so no screen changes. Used when the app is built with
 * VITE_API_MODE=agency; otherwise calls go straight to our API (`/cpaas`).
 *
 * What it does, route by route:
 * - path and verb differences (`/auth/login`, `…/brief`, `/content`, `PATCH`
 *   branding, `/audit`), and the request field names that API expects
 *   (`reason`, `feedback`, `content_ids`, `creator_id`, …);
 * - asks for whole lists (`per_page=100`) where the screens expect everything;
 * - fills in replies that are simpler than the screens' shapes (dashboard,
 *   Discovery, invoices, reports, audit, branding, content) with neutral
 *   defaults: fields the API doesn't send stay blank;
 * - transactions: the payment history from `payments/transactions`, a page at a time;
 * - payouts: none (that API lists batch summaries only), so the list is empty; the CSV
 *   upload goes to its validate/confirm;
 * - single payments: wallet-to-wallet through `payments` → `payments/otp`; other
 *   channels, payees and the payment list aren't there;
 * - wallets: the agency's funding wallets from `/wallets`, renamed, activated,
 *   deactivated or deleted there too (no top-ups or funding instructions, so
 *   funding a wallet is refused rather than showing sample ones);
 * - bulk payments: funding wallets from `/wallets`, payable creators from the
 *   campaign rosters plus `bulk-payments/preview`, and the batch through
 *   `bulk-payments/confirm` → `otp` (or `cancel`);
 * - the campaign roster: add-creator (a CSV is sent row by row), hire, respond
 *   and the early payout review, in that API's field names;
 * - contracts: built from the roster's contract status and the campaign's terms;
 * - performance: approved content and creators from the roster (no metrics);
 * - routes that API doesn't have yet (applications,
 *   creator profile, branding reset/verify/public, invoice hold, the outbox,
 *   batch history, scheduling, Mobile Money, payment files, recipient lists)
 *   answer empty or "not supported" instead of failing the whole page.
 * The differences are listed in docs/API_ENDPOINTS.md.
 */
import { africanMarkets } from '@/mocks/markets';
import { ApiError } from './apiError';

type Init = NonNullable<Parameters<typeof fetch>[1]>;
/** Sends one request to the agency API and returns its whole JSON body. */
export type Send = (path: string, init: Init) => Promise<unknown>;

type Json = Record<string, unknown>;
type Body = { data?: unknown; meta?: unknown };

const LIFECYCLE: Record<string, string> = {
  draft: 'Draft', recruiting: 'Recruiting', active: 'Active', review: 'Review',
  paying: 'Paying', completed: 'Completed', archived: 'Archived',
};

const CREATOR_STATUS: Record<string, string> = {
  shortlisted: 'Shortlisted', invited: 'Invited', clicked: 'Link opened', kyc_completed: 'KYC done',
  brief_sent: 'Brief sent', accepted: 'Accepted', negotiating: 'Negotiating', declined: 'Declined',
  contract_pending: 'Contract pending', signed: 'Contracted', contracted: 'Contracted', hired: 'Active',
  active: 'Active', deliverable_submitted: 'Content submitted', revision_requested: 'Revision needed',
  content_approved: 'Content approved', payable: 'Payable', payment_processing: 'Paying', paid: 'Paid',
  completed: 'Completed', removed: 'Removed',
};

const INVOICE_STATUS: Record<string, string> = {
  PENDING: 'Pending review', VALIDATED: 'Validated', APPROVED: 'Approved', REJECTED: 'Rejected',
  PARTIALLY_PAID: 'Partially Paid', PAID: 'Paid', EXPIRED: 'Expired',
};

/** The New campaign screen's content-type ids → production's labels (CpaasCampaign::CONTENT_TYPES). */
const CONTENT_TYPE_LABELS: Record<string, string> = {
  'instagram-post': 'Instagram Post', 'instagram-reel': 'Instagram Reel', 'instagram-story': 'Instagram Story',
  'tiktok-video': 'TikTok Video', 'youtube-video': 'YouTube Video', 'youtube-shorts': 'YouTube Shorts',
  'x-post': 'X Post', 'facebook-post': 'Facebook Post', 'linkedin-post': 'LinkedIn Post', 'blog-post': 'Blog Post',
  'podcast-mention': 'Podcast Mention', 'event-appearance': 'Event Appearance', other: 'Other',
};

const str = (value: unknown) => (value === null || value === undefined ? '' : String(value));
const num = (value: unknown) => Number(value ?? 0) || 0;
const arr = <T = Json>(value: unknown) => (Array.isArray(value) ? (value as T[]) : []);
const dateOnly = (value: unknown) => str(value).slice(0, 10);
const humanise = (value: string) => value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const notSupported = (what: string) =>
  new ApiError(501, 'not_supported', `${what} isn't available from the production API yet.`);

/** A list reply in the screens' `{ data, meta }` form. */
const list = (data: unknown[]) => ({
  data,
  meta: { page: 1, per_page: data.length, total: data.length, total_pages: 1 },
});

function withPerPage(path: string, perPage = 100) {
  const url = new URL(path, 'http://x');
  if (!url.searchParams.has('per_page')) url.searchParams.set('per_page', String(perPage));
  return `${url.pathname}${url.search}`;
}

// ---------------------------------------------------------------------------
// Reply conversions
// ---------------------------------------------------------------------------

function toCampaign(c: Json) {
  const status = str(c.status);
  return {
    id: str(c.id), name: str(c.name), brandClient: str(c.brandClient), objective: str(c.objective),
    description: str(c.description), markets: arr<string>(c.markets), platforms: arr<string>(c.platforms),
    currency: str(c.currency) || 'KES', budget: num(c.budget), budgetPerCreator: num(c.budgetPerCreator),
    spend: num(c.spend), status: LIFECYCLE[status] ?? status, startDate: str(c.startDate), endDate: str(c.endDate),
    creatorsTotal: num(c.creatorsTotal), creatorsActive: num(c.creatorsActive), progress: num(c.progress),
    flags: arr<string>(c.flags).map((flag) => (flag === 'Deadline at risk' ? 'Deadline missed' : flag)),
    aiSummary: str(c.aiSummary), inviteOnly: Boolean(c.inviteOnly),
    linkExpiresOn: dateOnly(c.linkExpiresOn) || undefined,
    // The agency API sends no link status; "On hold" is its flag for a disabled campaign.
    linkEnabled: !arr<string>(c.flags).includes('On hold'),
  };
}

/** The detail reply adds the brief and the onboarding link; it has no raw content types. */
function toCampaignDetail(c: Json) {
  return {
    ...toCampaign(c),
    deliverables: str(c.deliverables), terms: str(c.terms),
    paymentTerms: c.paymentTerms === null || c.paymentTerms === undefined ? null : num(c.paymentTerms),
    contractDate: dateOnly(c.contractDate) || null, onboardingLink: str(c.onboardingLink), linkClicks: num(c.linkClicks),
    // The agency join page box; absent until the backend sends them (twiva-server join endpoints MR).
    agencyJoinRef: str(c.agencyJoinRef) || undefined, onboardingToken: str(c.onboardingToken) || undefined,
  };
}

function toFlagged(row: Json) {
  const status = str(row.campaignStatus || row.status);
  return {
    id: str(row.id), creatorName: str(row.creatorName), creatorId: '', avatar: null,
    campaign: str(row.campaignName), campaignId: str(row.campaignId),
    campaignStatus: LIFECYCLE[status] ?? CREATOR_STATUS[status] ?? status,
    action: str(row.action), flag: str(row.flag), due: str(row.due),
  };
}

function toDashboard(d: Json) {
  const k = (d.kpis ?? {}) as Json;
  const markets = arr<string>(k.marketsInFlight);
  const progress = arr(d.campaignProgress);
  const nudge = (d.applicationNudge ?? {}) as Json;
  return {
    stats: {
      activeCampaigns: num(k.activeCampaigns), inReviewCampaigns: num(k.inReview),
      marketsInFlight: markets.length, inFlightMarkets: markets,
      creatorsNeedingAttention: num(k.creatorsNeedingAttention), pendingApprovals: num(k.pendingApprovals),
      approvalsDueToday: 0, approvalsHint: '',
    },
    spend: { thisMonth: num(k.spendThisMonth), lastMonth: num(k.spendLastMonth), currency: str(progress[0]?.currency) || 'KES' },
    campaigns: progress.map(toCampaign),
    creatorActions: arr(d.creatorsNeedingAttention).map(toFlagged),
    notYetActive: arr(d.notYetActive).map(toFlagged),
    approvals: arr(d.pendingApprovalItems).map((item, index) => ({
      id: `apv-${index}`, type: str(item.type), title: str(item.title), detail: str(item.detail),
      amount: str(item.amount), status: str(item.status), due: str(item.due), link: str(item.link) || undefined,
    })),
    dueToday: [],
    insights: arr(d.insights),
    alerts: arr(d.alerts),
    applications: {
      count: num(nudge.count),
      campaigns: arr(nudge.campaigns).map((c) => ({ id: str(c.id), name: str(c.name), count: num(c.applicantCount ?? c.count) })),
    },
    scheduledBatches: [],
  };
}

function toInvoice(i: Json) {
  const campaign = (i.campaign ?? null) as Json | null;
  const amount = num(i.amount);
  const status = str(i.status);
  const trail = [
    { id: 'created', actor: str(i.recipientName) || 'Creator', action: 'Invoice submitted', at: str(i.createdAt) },
    ...(i.approvedAt ? [{ id: 'approved', actor: str(i.approvedBy) || 'Agency', action: 'Approved invoice', at: str(i.approvedAt) }] : []),
    ...(i.rejectedAt ? [{ id: 'rejected', actor: str(i.rejectedBy) || 'Agency', action: 'Rejected invoice', at: str(i.rejectedAt), note: str(i.rejectionNote) || undefined }] : []),
  ];
  return {
    id: str(i.id), number: str(i.invoiceNumber), creatorId: '', creatorName: str(i.recipientName), avatar: null,
    campaignId: campaign ? str(campaign.id) : '', campaign: campaign ? str(campaign.name) : 'Standalone invoice',
    market: '', currency: str(i.currency) || 'KES', subtotal: amount, tax: 0, amount,
    status: INVOICE_STATUS[status] ?? status, issuedAt: dateOnly(i.createdAt), dueAt: '', deliverableRef: '',
    rail: '', walletStatus: '', kycStatus: '', notes: str(i.rejectionNote), flags: [],
    lineItems: [{ id: 'line-1', description: 'Invoice amount', detail: '', amount }],
    checks: [], trail,
  };
}

function toCreatorProfile(c: Json) {
  return {
    id: str(c.id), name: str(c.name),
    // The agency API puts the email in `handle`; don't show it as a handle.
    handle: str(c.handle).includes('@') && !str(c.handle).startsWith('@') ? '' : str(c.handle),
    avatar: (c.avatar as string | null) ?? null, city: '', markets: [], categories: [], languages: [],
    platforms: [], contentFormats: [], followers: num(c.followers), engagementRate: num(c.engagementRate),
    rate: null, currency: 'KES', verification: c.kycVerified ? 'Verified' : 'Unverified',
    kycStatus: c.kycVerified ? 'KYC complete' : 'KYC incomplete',
    walletStatus: c.walletActive ? 'Wallet active' : 'Wallet inactive',
    audience: null, brandSafety: '', responseTime: '',
    agencyHistory: { campaigns: 0, onTimeRate: null, rating: null },
    fitScore: null, reasonCodes: [], riskFlags: [], bio: '',
  };
}

/** CampaignContentController's approval labels → the deliverable statuses the screens group on. */
const CONTENT_STATUS: Record<string, string> = {
  Pending: 'Deliverable Submitted', 'Revision requested': 'Changes requested', Approved: 'Approved', Rejected: 'Rejected',
};
const PAID_CREATOR_STATUSES = ['payable', 'payment_processing', 'paid', 'completed'];
/** Roster statuses that owe content (hired, before submitting). */
const OWES_CONTENT = ['signed', 'contracted', 'hired', 'active'];

/**
 * One content row (CampaignContentController::toContent) as a deliverable, joined to its
 * roster row for the creator's id, photo and paid status, and to its revisions when known.
 * The agency API returns no media URL, so there's nothing to preview yet.
 */
function toDeliverable(c: Json, campaignId: string, rosterRow?: Json, revisions: Json[] = []) {
  const creator = (rosterRow?.creator ?? {}) as Json;
  const label = str(c.approvalStatus);
  const paid = PAID_CREATOR_STATUSES.includes(str(rosterRow?.statusKey));
  const status = paid && label === 'Approved' ? 'Paid' : CONTENT_STATUS[label] ?? 'Deliverable Submitted';
  const submittedAt = str(c.submittedAt);
  const caption = str(c.caption);
  const feedback = [
    ...revisions.flatMap((r) => [
      { id: `rev-${str(r.id)}`, author: 'Agency', authorRole: 'Agency', at: str(r.at), text: str(r.feedback), decision: 'changes_requested' },
      ...(r.response ? [{ id: `rev-${str(r.id)}-reply`, author: str(c.creatorName), authorRole: 'Creator', at: str(r.at), text: str(r.response) }] : []),
    ]),
    ...(label === 'Approved'
      ? [{ id: `dlv-${str(c.id)}-approved`, author: str(c.reviewedBy) || 'Agency', authorRole: 'Agency', at: str(c.reviewedAt) || submittedAt, text: str(c.reviewNote) || 'Approved this deliverable.', decision: 'approved' }]
      : []),
    ...(label === 'Rejected'
      ? [{ id: `dlv-${str(c.id)}-rejected`, author: str(c.reviewedBy) || 'Agency', authorRole: 'Agency', at: str(c.reviewedAt) || submittedAt, text: str(c.reviewNote) || 'Rejected.', decision: 'rejected' }]
      : []),
  ];
  return {
    id: str(c.id), campaignId,
    creatorId: str(rosterRow?.creatorId) || str(c.campaignCreatorId),
    creatorName: str(c.creatorName) || str(creator.name),
    avatar: (creator.avatar as string | null) ?? null,
    type: str(c.platform) || 'Content', platform: str(c.platform), dueDate: '', status, flag: '',
    review: {
      mediaUrl: str(c.contentUrl), mediaKind: '', duration: '', aspectRatio: '',
      submittedAt: submittedAt.slice(0, 10), caption,
      hashtags: caption.match(/#[\p{L}\p{N}_]+/gu) ?? [], checks: [],
      versions: [{ id: `dlv-${str(c.id)}-v1`, version: 1, mediaUrl: str(c.contentUrl), submittedAt: submittedAt.slice(0, 10), note: 'Submission' }],
      feedback, postUrl: null,
    },
  };
}

function toAuditEvent(row: Json) {
  const actorType = str(row.actorType) || 'system';
  const action = str(row.action);
  const from = str(row.fromStatus);
  const to = str(row.toStatus);
  const changes = [
    ...(from || to ? [{ field: 'Creator status', before: CREATOR_STATUS[from] ?? (from || '—'), after: CREATOR_STATUS[to] ?? (to || '—') }] : []),
    ...(row.notes ? [{ field: 'Note', before: '—', after: str(row.notes) }] : []),
  ];
  const creator = str(row.creatorName) || 'a creator';
  const campaign = str(row.campaignName);
  const summaries: Record<string, string> = {
    invited: `invited ${creator} to ${campaign}`,
    re_invited: `re-invited ${creator} to ${campaign}`,
    clicked: `opened the invite to ${campaign}`,
    app_joined: `applied to ${campaign} in the Twiva app`,
    removed: `removed ${creator} from ${campaign}`,
    mark_paid: `marked ${creator} as paid on ${campaign}`,
    bulk_mark_processing: `sent ${creator}'s payment for processing on ${campaign}`,
  };
  return {
    id: `aud-${str(row.id)}`, actorId: actorType, actorName: str(row.actorName) || humanise(actorType),
    actorAvatar: '', actorRole: actorType === 'agency' ? 'Staff' : actorType === 'creator' ? 'Creator' : 'System',
    action: humanise(action), summary: summaries[action] ?? `${humanise(action).toLowerCase()} · ${creator} on ${campaign}`,
    category: ['mark_paid', 'bulk_mark_processing'].includes(action) ? 'Money' : 'Campaign',
    resource: 'Campaign', resourceId: campaign, impact: 'Standard', outcome: 'Success', market: '', ip: '—',
    source: actorType === 'agency' ? 'Agency portal' : actorType === 'creator' ? 'Creator portal' : 'System',
    reference: `LOG-${str(row.id)}`, timestamp: str(row.at).replace(' ', 'T'), changes,
  };
}

/** Branding (`/settings/branding`) plus the agency details only `/profile` sends: initials, currency, markets, domain. */
function toBranding(b: Json, agency: Json = {}) {
  const name = str(b.name);
  const initials = name.split(/\s+/).filter(Boolean).map((word) => word[0]).join('').slice(0, 2).toUpperCase();
  return {
    profile: {
      name, legalName: '', tagline: str(b.tagline), supportEmail: '', website: str(b.website), baseCurrency: str(agency.baseCurrency) || 'KES',
      markets: arr<string>(Array.isArray(b.markets) ? b.markets : agency.markets), logoInitials: str(agency.logoInitials) || initials, domain: str(agency.domain),
      domainStatus: 'Not connected',
    },
    colours: {
      primary: (b.primaryColor as string | null) ?? null,
      accent: (b.accentColor as string | null) ?? null,
      secondary: (b.secondaryColor as string | null) ?? null,
    },
    logos: b.logoUrl ? { mark: str(b.logoUrl) } : {},
  };
}

/** The first market that settles in a currency (KES → Kenya). */
const marketFor = (currency: string) => africanMarkets.find((market) => market.currency === currency)?.name ?? '';

/** The agency's ledger wallet: `availableBalance` can be spent; the rest of `currentBalance` is held. */
function toFundingWallet(w: Json) {
  const currency = str(w.currency) || 'KES';
  const available = num(w.availableBalance ?? w.balance);
  return {
    id: str(w.id), ownerType: 'Funding', ownerId: 'agency', ownerName: str(w.name), description: str(w.description), market: marketFor(currency),
    currency, balance: available, pending: Math.max(0, num(w.currentBalance ?? available) - available),
    status: str(w.status) === 'inactive' ? 'Wallet inactive' : 'Wallet active', lastActivity: dateOnly(w.createdAt),
  };
}

/**
 * The backend credits the ledger both for top-ups (an admin's `topUpLedger`, with the admin's
 * description) and to hand back failed or abandoned payments ("Refund — …", "Reversal: …").
 */
const isLedgerRefund = (description: string) => /^(refund|reversal)\b/i.test(description.trim());

/** A Twiva top-up of the agency's ledger, from its credits that aren't refunds. */
function toLedgerTopUp(t: Json, walletId: string) {
  return {
    id: `topup-${str(t.id)}`, reference: str(t.reference) || `TOPUP-${str(t.id)}`, walletId, currency: 'KES',
    amount: num(t.amount), method: str(t.description) || 'Top-up by Twiva', status: 'Completed',
    createdAt: str(t.createdAt).replace(' ', 'T'), by: 'Twiva',
  };
}

/**
 * A line of the agency's ledger (`payments/transactions`): a credit or debit with the
 * balance after it. It names no payee, currency or channel, and is written only once
 * the transfer has gone through, so it shows as completed.
 */
function toTransaction(t: Json) {
  const credit = str(t.type) === 'credit';
  const description = str(t.description);
  const refund = credit && isLedgerRefund(description);
  return {
    id: str(t.id), reference: str(t.reference) || str(t.id), description,
    party: refund ? 'Refund' : credit ? 'Twiva pool wallet' : description || 'Creator payout', partyType: credit ? 'Funding' : 'Twiva wallet',
    origin: refund ? 'reversal' : credit ? 'topup' : description === 'Bulk creator payout' ? 'bulk' : 'single', channel: 'wallet',
    direction: credit ? 'Credit' : 'Debit', amount: num(t.amount),
    currency: 'KES', wallet: 'Funding · KES', rail: 'Twiva wallet · KES',
    status: 'Completed', recon: 'Pending', createdAt: str(t.createdAt).replace(' ', 'T'),
  };
}

const SINGLE_CHANNELS: Record<string, string> = {
  mobile: 'mobile money', bank: 'bank transfer', bill: 'paybill', airtime: 'airtime', goods: 'buy goods',
};

/** A sent payment as the receipt shows it; the agency API returns no payment record. */
function toSinglePayment(payment: Json) {
  const destination = (payment.destination ?? {}) as Json;
  return {
    id: `sp-${Date.now()}`, reference: '', payeeName: str(payment.payeeName), avatar: '', channel: 'wallet',
    target: `Wallet ${str(destination.walletId)}`, amount: num(payment.amount), currency: str(payment.currency),
    rail: `Twiva wallet · ${str(payment.currency)}`, status: 'Processing', createdAt: new Date().toISOString(), createdBy: '',
  };
}

/** A submitted batch as the history shows it; the agency API keeps no batch record. */
function toBulkTransfer(batch: Json) {
  const rows = arr(batch.recipients);
  return {
    id: `bt-${Date.now()}`, reference: '', channel: str(batch.channel) || 'wallet', sourceWalletName: str(batch.sourceWalletName),
    currency: str(batch.currency), recipientCount: rows.length, total: rows.reduce((sum, row) => sum + num(row.amount), 0),
    sweep: 0, status: 'Processing', createdAt: new Date().toISOString(), createdBy: '',
  };
}

/**
 * Creators on the agency's campaigns with an active Twiva Pay wallet whose name or
 * email matches `q`, as payees. payments/creators/search is scoped to the agency and
 * returns Twiva Pay wallets only since twiva-server 722ce6b7d (BACKEND_GAPS #50).
 */
async function searchWalletHolders(get: (to: string) => Promise<Body>, q: string) {
  if (q.trim().length < 2) return [];
  const hits = arr((await get(`/payments/creators/search?q=${encodeURIComponent(q.trim())}`)).data);
  return hits
    .filter((hit) => str(hit.walletType) === 'twiva_pay' && str(hit.walletId))
    .map((hit) => ({
      id: `wallet-${str(hit.walletId)}`, kind: 'creator', creatorId: str(hit.userId), name: str(hit.name) || str(hit.email),
      avatar: '', handle: str(hit.email) || undefined, market: marketFor('KES'), currency: 'KES', invoices: [],
      target: { walletId: str(hit.walletId) }, status: 'Wallet active',
    }));
}

/** Roster statuses after hiring: creators who will be paid, are being paid, or were paid. */
const HIRED_STATUSES = [
  'signed', 'contracted', 'hired', 'active', 'deliverable_submitted', 'revision_requested',
  'content_approved', 'payable', 'payment_processing', 'paid', 'completed',
];

/** Why a hired creator isn't payable yet, so a search for them explains itself. */
function waitingReason(row: Json): string {
  const status = str(row.statusKey);
  if (!HIRED_STATUSES.includes(status)) return `Not hired yet (${CREATOR_STATUS[status] ?? status})`;
  const done = num(row.deliverablesDone);
  const total = num(row.deliverablesTotal);
  const count = total > 0 ? ` (${done} of ${total} approved)` : '';
  if (status === 'payment_processing') return 'Payment in progress';
  if (status === 'paid' || status === 'completed') return 'Already paid';
  if (status === 'content_approved') {
    // Content approved before twiva-server 722ce6b7d never moved the creator on to payable (BACKEND_GAPS #53).
    return total > 0 && done < total ? `Content not all approved yet${count}` : 'Content approved, but not marked payable yet';
  }
  if (status === 'revision_requested') return `Changes requested on their content${count}`;
  if (status === 'deliverable_submitted') return `Content waiting for approval${count}`;
  return `Hired; content not approved yet${count}`;
}

/**
 * Everyone the agency can pay: creators marked payable on its campaigns (each
 * campaign's roster), with the wallet and amount bulk-payments/preview resolves
 * for them. Creators preview can't resolve stay listed, blocked with the reason.
 * Hired creators who aren't payable yet come back too, with `waiting` saying why,
 * and creators not hired yet (`notHired`), so a campaign shows everyone on it.
 */
async function bulkCandidates(
  get: (to: string) => Promise<Body>,
  call: (to: string, verb: string, payload?: unknown) => Promise<Body>,
  sourceWalletId: string,
  currency: string,
) {
  const campaigns = arr((await get(withPerPage('/campaigns'))).data)
    .filter((c) => !currency || (str(c.currency) || 'KES') === currency);
  const rosters = await Promise.all(
    campaigns.map(async (c) => ({
      campaign: c,
      rows: arr((await get(withPerPage(`/campaigns/${str(c.id)}/creators`))).data)
        .filter((row) => !['declined', 'removed'].includes(str(row.statusKey))),
    })),
  );
  const hired = rosters.flatMap(({ campaign, rows }) => rows.map((row) => ({ campaign, row })));
  const payable = hired.filter(({ row }) => str(row.statusKey) === 'payable');
  const waiting = hired
    .filter(({ row }) => str(row.statusKey) !== 'payable')
    .map(({ campaign, row }) => {
      const currency = str(row.currency) || 'KES';
      return {
        id: str(row.id), name: str((row.creator as Json | undefined)?.name), avatar: str((row.creator as Json | undefined)?.avatar),
        walletId: '', walletType: 'Ledger', market: marketFor(currency), currency, suggestedAmount: num(row.rate),
        campaign: str(campaign.name), waiting: waitingReason(row),
        notHired: !HIRED_STATUSES.includes(str(row.statusKey)) || undefined,
      };
    });
  if (payable.length === 0 || !sourceWalletId) return waiting;

  const resolved = new Map<string, Json>();
  let walletErrors = new Set<string>();
  let previewError = '';
  try {
    const preview = ((await call('/bulk-payments/preview', 'POST', { from_wallet_id: Number(sourceWalletId) })).data ?? {}) as Json;
    arr(preview.transactions).forEach((t) => resolved.set(`cc-${str(t.ccId)}`, t));
    walletErrors = new Set(arr<string>(preview.walletErrors));
  } catch (error) {
    previewError = error instanceof Error ? error.message : 'unknown error';
  }

  const ready = payable.map(({ campaign, row }) => {
    const id = str(row.id);
    const creator = (row.creator ?? {}) as Json;
    const t = resolved.get(id);
    const rowCurrency = str(row.currency) || 'KES';
    return {
      id, name: str(creator.name), avatar: str(creator.avatar), walletId: t ? str(t.toWalletId) : '', walletType: 'Ledger',
      market: marketFor(rowCurrency), currency: rowCurrency, suggestedAmount: t ? num(t.amount) : num(row.rate),
      campaign: str(campaign.name),
      blocked: t
        ? undefined
        : previewError
          ? `The production API couldn't look up this creator's wallet (${previewError})`
          : walletErrors.has(id) ? 'No Twiva wallet' : 'Not payable from this wallet',
    };
  });
  return [...ready, ...waiting];
}

// ---------------------------------------------------------------------------
// Routing
// ---------------------------------------------------------------------------

/** Handles one call from the screens; returns the reply body the screens expect. */
/** Laravel's Discovery follower tiers (InfluencerDiscoveryController::TIER_RANGES). */
function tierOf(followers: number) {
  if (followers < 10_000) return 'nano';
  if (followers < 50_000) return 'micro';
  if (followers < 500_000) return 'mid';
  if (followers < 1_000_000) return 'macro';
  return 'mega';
}

/**
 * Discovery rows per query for a minute, so changing a filter the app applies itself
 * (tier, engagement, sort) re-filters at once instead of asking the server again.
 */
const discoveryCache = new Map<string, { at: number; reply: Promise<Body> }>();
function discoveryRows(path: string, get: (path: string) => Promise<Body>): Promise<Body> {
  const hit = discoveryCache.get(path);
  if (hit && Date.now() - hit.at < 60_000) return hit.reply;
  const reply = get(path);
  discoveryCache.set(path, { at: Date.now(), reply });
  reply.catch(() => discoveryCache.delete(path));
  return reply;
}

/**
 * A creator's campaigns with this agency, built from each campaign's roster (the agency
 * API has no per-creator route). Rosters are cached for a minute; six fetched at a time.
 */
const rosterCache = new Map<string, { at: number; rows: Promise<Json[]> }>();
async function creatorCampaigns(userId: string, get: (path: string) => Promise<Body>) {
  const campaigns = arr((await get(withPerPage('/campaigns'))).data);
  const rosterOf = (campaignId: string) => {
    const hit = rosterCache.get(campaignId);
    if (hit && Date.now() - hit.at < 60_000) return hit.rows;
    const rows = get(withPerPage(`/campaigns/${campaignId}/creators`)).then((reply) => arr(reply.data));
    rosterCache.set(campaignId, { at: Date.now(), rows });
    rows.catch(() => rosterCache.delete(campaignId));
    return rows;
  };
  const found: Json[] = [];
  for (let start = 0; start < campaigns.length; start += 6) {
    const batch = campaigns.slice(start, start + 6);
    const rosters = await Promise.all(batch.map((c) => rosterOf(str(c.id)).catch(() => [] as Json[])));
    batch.forEach((campaign, index) => {
      for (const row of rosters[index]) {
        if (str(row.creatorId) !== userId) continue;
        found.push({
          rosterId: str(row.id), campaignId: str(campaign.id), campaignName: str(campaign.name),
          campaignStatus: LIFECYCLE[str(campaign.status)] ?? str(campaign.status),
          status: str(row.status), rate: num(row.rate), currency: str(row.currency) || 'KES',
          deliverablesDone: num(row.deliverablesDone), deliverablesTotal: num(row.deliverablesTotal),
        });
      }
    });
  }
  return found;
}

export async function agencyRequest(path: string, init: Init, send: Send): Promise<unknown> {
  const method = (init.method ?? 'GET').toUpperCase();
  const body = (init.body ? JSON.parse(String(init.body)) : {}) as Json;
  const url = new URL(path, 'http://x');
  const p = url.pathname;
  const call = (to: string, verb: string, payload?: unknown) =>
    send(to, { ...init, method: verb, body: payload === undefined ? undefined : JSON.stringify(payload) }) as Promise<Body>;
  const get = (to: string) => call(to, 'GET');
  /** A route added in SCRUM-151 round 3; null while production doesn't have it yet (404/405). */
  const newRoute = async (load: () => Promise<Body>): Promise<Body | null> => {
    try {
      return await load();
    } catch (error) {
      if (error instanceof ApiError && (error.status === 404 || error.status === 405)) return null;
      throw error;
    }
  };

  /** A campaign's deliverables: submitted content plus hired creators who still owe some. */
  const campaignDeliverables = async (campaignId: string) => {
    const [content, roster] = await Promise.all([
      get(withPerPage(`/campaigns/${campaignId}/content`)),
      get(withPerPage(`/campaigns/${campaignId}/creators`)).catch(() => ({ data: [] }) as Body),
    ]);
    const rows = arr(content.data);
    const rosterById = new Map(arr(roster.data).map((row) => [str(row.id), row]));
    // The change-request thread is only on the single-content route.
    const threads = new Map<string, Json[]>(
      await Promise.all(
        rows
          .filter((c) => str(c.approvalStatus) === 'Revision requested')
          .map(async (c) => {
            const one = await get(`/campaigns/${campaignId}/content/${str(c.id)}`).catch(() => ({ data: {} }) as Body);
            return [str(c.id), arr(((one.data ?? {}) as Json).revisions)] as [string, Json[]];
          }),
      ),
    );
    const delivered = rows.map((c) => toDeliverable(c, campaignId, rosterById.get(str(c.campaignCreatorId)), threads.get(str(c.id))));
    const withContent = new Set(rows.map((c) => str(c.campaignCreatorId)));
    const owing = arr(roster.data)
      .filter((row) => OWES_CONTENT.includes(str(row.statusKey)) && !withContent.has(str(row.id)))
      .map((row) => {
        const creator = (row.creator ?? {}) as Json;
        return {
          id: `owed-${str(row.id)}`, campaignId, creatorId: str(row.creatorId), creatorName: str(creator.name),
          avatar: (creator.avatar as string | null) ?? null, type: 'Content', platform: '', dueDate: '',
          status: arr<string>(row.flags).includes('Deadline missed') ? 'Deliverable missing' : 'Draft', flag: '', review: null,
        };
      });
    return [...delivered, ...owing];
  };
  let m: RegExpMatchArray | null;

  // Sign-in and the signed-in user
  if (method === 'POST' && p === '/login') return call('/auth/login', 'POST', body);
  // Password reset (round 3, #39): the emailed link is /reset-password?token=…&email=…;
  // the token is only checked when the new password is sent.
  if (method === 'POST' && p === '/forgot-password') {
    const reply = await newRoute(() => call('/auth/forgot-password', 'POST', { email: body.email }));
    if (!reply) {
      throw new ApiError(501, 'not_supported', 'Password reset isn\'t on the production API yet. Reset it at twiva.com/cpaas/forgot-password, then sign in here.');
    }
    return { data: { message: str(((reply.data ?? {}) as Json).message) || 'If that email is registered, a reset link has been sent.' } };
  }
  if (method === 'GET' && p.startsWith('/reset-password/')) return { data: { valid: true } };
  if (method === 'POST' && p === '/reset-password') {
    const reply = await call('/auth/reset-password', 'POST', {
      email: body.email, token: body.token, password: body.password, password_confirmation: body.passwordConfirmation,
    });
    return { data: { message: str(((reply.data ?? {}) as Json).message) || 'Password reset. Sign in with your new password.' } };
  }
  if (method === 'POST' && p === '/password/change') {
    return call('/password/change', 'POST', { password: body.password, password_confirmation: body.passwordConfirmation });
  }

  if (method === 'GET' && p === '/dashboard') return { data: toDashboard(((await get(p)).data ?? {}) as Json) };

  if (method === 'GET' && p === '/search') {
    const d = ((await get(path)).data ?? {}) as Json;
    return {
      data: {
        campaigns: arr(d.campaigns).map((c) => ({ id: str(c.id), name: str(c.name), status: LIFECYCLE[str(c.status)] ?? str(c.status), markets: arr<string>(c.markets) })),
        // Roster rows open their campaign's Creators tab; platform-wide matches (round 3,
        // `u-<user id>`, no campaign) open the creator's profile.
        creators: arr(d.creators).map((c) => ({
          id: str(c.id).replace(/^u-/, ''), name: str(c.name), handle: c.username ? `@${str(c.username)}` : '', city: '',
          campaignId: str(c.campaignId), campaignName: str(c.campaignName),
        })),
        invoices: arr(d.invoices).map((i) => ({
          id: str(i.id), number: str(i.invoiceNumber), creatorName: str(i.recipientName), campaign: str(i.campaignName),
          amount: `${str(i.currency) || 'KES'} ${num(i.amount).toLocaleString()}`,
        })),
      },
    };
  }

  // Campaigns
  if (method === 'GET' && p === '/campaigns') {
    const reply = await get(withPerPage(path));
    return { data: arr(reply.data).map(toCampaign), meta: reply.meta };
  }
  // Brief extraction (AI). The route carries a campaign id that the API never reads.
  if (method === 'POST' && p === '/campaigns/extract-brief') {
    return { data: ((await call('/campaigns/0/extract-brief', 'POST', { brief: body.brief })).data ?? {}) as Json };
  }
  if (method === 'POST' && p === '/campaigns') {
    const brief = (body.brief ?? {}) as Json;
    const creatorIds = arr<string>(body.creatorIds);
    const budget = num(brief.budget);
    const created = ((await call('/campaigns', 'POST', {
      name: brief.name, brand_client: brief.brandClient || null, objective: brief.objective || null,
      description: brief.description || null, deliverables: brief.deliverables || null, terms: body.contractTerms || null,
      // "Other" is saved as the text typed for it, when there is one (production accepts any label).
      content_type: arr<string>(brief.contentTypes).map((id) => (id === 'other' && str(brief.customContentType).trim()) || CONTENT_TYPE_LABELS[id] || id),
      markets: arr<string>(brief.markets),
      budget_per_creator: budget > 0 ? Math.round((budget / Math.max(1, creatorIds.length)) * 100) / 100 : 0,
      budget_currency: brief.currency || 'KES', start_date: brief.startDate || null, end_date: brief.endDate || null,
      expires_at: brief.linkExpiresOn ? `${brief.linkExpiresOn} 23:59:59` : null, is_invite_only: Boolean(brief.inviteOnly),
      // Saved with the campaign; production's invitations still use their fixed text.
      invite_message: str(body.inviteMessage) || null,
    })).data ?? {}) as Json;
    // The agency API adds creators one at a time (and notifies them).
    let invited = 0;
    for (const id of creatorIds) {
      try {
        await call('/discovery/add-to-campaign', 'POST', { creator_id: Number(id), campaign_id: Number(created.id) });
        invited += 1;
      } catch {
        // Already on the campaign or unknown: skip, as our API does.
      }
    }
    return { data: { id: str(created.id), invited } };
  }
  if ((m = p.match(/^\/campaigns\/([^/]+)$/))) {
    if (method === 'GET' || method === 'PUT') return { data: toCampaignDetail(((await call(p, method, method === 'PUT' ? body : undefined)).data ?? {}) as Json) };
    // Edit campaign: the agency API's fullUpdate takes snake_case and changes only the fields sent.
    if (method === 'PATCH') {
      const fields: Record<string, string> = {
        name: 'name', description: 'description', deliverables: 'deliverables', terms: 'terms',
        budgetPerCreator: 'budget_per_creator', currency: 'budget_currency', contentTypes: 'content_type',
        markets: 'markets', startDate: 'start_date', endDate: 'end_date', contractDate: 'contract_date',
        linkExpiresOn: 'expires_at', paymentTerms: 'payment_terms', inviteOnly: 'is_invite_only',
      };
      const payload = Object.fromEntries(
        Object.entries(body).filter(([key]) => fields[key]).map(([key, value]) => [fields[key], value === '' ? null : value]),
      );
      return { data: toCampaignDetail(((await call(p, 'PATCH', payload)).data ?? {}) as Json) };
    }
  }
  if (method === 'POST' && (m = p.match(/^\/campaigns\/([^/]+)\/advance-lifecycle$/))) {
    return { data: toCampaignDetail(((await call(`/campaigns/${m[1]}/advance`, 'POST', {})).data ?? {}) as Json) };
  }
  if (method === 'POST' && /^\/campaigns\/[^/]+\/(disable|enable)$/.test(p)) {
    return { data: toCampaignDetail(((await call(p, 'POST', {})).data ?? {}) as Json) };
  }
  if (method === 'POST' && /^\/campaigns\/[^/]+\/(archive|reopen)$/.test(p)) {
    return { data: toCampaignDetail(((await call(p, 'POST', {})).data ?? {}) as Json) };
  }

  // Campaign tabs
  if (method === 'GET' && (m = p.match(/^\/campaigns\/([^/]+)\/(creators|activity)$/))) return get(withPerPage(path));
  if (method === 'GET' && (m = p.match(/^\/campaigns\/([^/]+)\/contents$/))) {
    try {
      return { data: await campaignDeliverables(m[1]) };
    } catch (error) {
      console.warn('[agency API] content list failed; showing no deliverables', error);
      return { data: [] };
    }
  }
  // The roster's contractStatus (Signed / Awaiting signature, from each creator's status) and
  // the campaign's terms make the list; the contracts route (round 3, #46) adds when each
  // contract was sent and signed.
  if (method === 'GET' && (m = p.match(/^\/campaigns\/([^/]+)\/contracts$/))) {
    const [roster, campaign, dated] = await Promise.all([
      get(withPerPage(`/campaigns/${m[1]}/creators`)),
      get(`/campaigns/${m[1]}`).catch(() => ({ data: {} }) as Body),
      newRoute(() => get(`/campaigns/${m![1]}/contracts`)).catch(() => null),
    ]);
    const dates = new Map(arr(dated?.data).map((row) => [str(row.id), row]));
    const terms = str(((campaign.data ?? {}) as Json).terms);
    const contracts = arr(roster.data)
      .filter((row) => ['Signed', 'Awaiting signature'].includes(str(row.contractStatus)))
      .map((row) => {
        const creator = (row.creator ?? {}) as Json;
        const name = str(creator.name) || 'Creator';
        const when = (dates.get(str(row.id)) ?? {}) as Json;
        return {
          ...(when.sentAt ? { sentAt: str(when.sentAt) } : {}),
          ...(when.signedAt ? { signedAt: str(when.signedAt) } : {}),
          id: `ct-${str(row.id)}`, campaignId: m![1], creatorId: str(row.creatorId), creatorName: name,
          avatar: (creator.avatar as string | null) ?? null,
          fileName: `Contract - ${name}.pdf`, fileType: 'PDF', fileSize: '',
          currency: str(row.currency) || 'KES', amount: num(row.rate), deliverables: num(row.deliverablesTotal),
          status: str(row.contractStatus) === 'Signed' ? 'Signed' : 'Sent',
          handle: str(creator.handle), body: terms,
        };
      });
    return { data: contracts };
  }
  if (method === 'GET' && /^\/campaigns\/[^/]+\/applications$/.test(p)) return { data: [] };
  // Approved content and the creators come from the roster. The performance route (round 3,
  // #49) adds each post's views and engagement from content.performance_data; it has no reach
  // or impressions, so those stay empty. Without it everything but the counts stays empty.
  if (method === 'GET' && (m = p.match(/^\/campaigns\/([^/]+)\/performance$/))) {
    const [roster, perf] = await Promise.all([
      get(withPerPage(`/campaigns/${m[1]}/creators`)),
      newRoute(() => get(`/campaigns/${m![1]}/performance`)).catch(() => null),
    ]);
    const rows = arr(roster.data).filter((row) => num(row.deliverablesTotal) > 0 || OWES_CONTENT.includes(str(row.statusKey)));
    const posts = arr(perf?.data);
    const metrics = new Map<string, { views: number; engagements: number }>();
    for (const post of posts) {
      const stats = (post.performance ?? {}) as Json;
      const key = str(post.creatorId);
      const sum = metrics.get(key) ?? { views: 0, engagements: 0 };
      metrics.set(key, { views: sum.views + num(stats.views), engagements: sum.engagements + num(stats.engagements) });
    }
    const rate = (engagements: number, views: number) => (views > 0 ? Math.round((engagements / views) * 1000) / 10 : null);
    const summary = (perf?.summary ?? {}) as Json;
    const views = num(summary.totalViews);
    const engagements = num(summary.totalEngagements);
    return {
      data: {
        approvedContent: rows.reduce((sum, row) => sum + num(row.deliverablesDone), 0),
        contentWithData: posts.filter((post) => Object.values((post.performance ?? {}) as Json).some((value) => num(value) > 0)).length,
        lastSyncedAt: null,
        totals: { reach: 0, views, impressions: 0, engagements, engagementRate: rate(engagements, views) },
        reachDeliveredPct: null,
        engagementVsBenchmarkPct: null,
        creators: rows
          .map((row) => {
            const creator = (row.creator ?? {}) as Json;
            const own = metrics.get(str(creator.id)) ?? { views: 0, engagements: 0 };
            return {
              creatorId: str(row.creatorId), name: str(creator.name), avatar: (creator.avatar as string | null) ?? null,
              followers: num(creator.followers), posts: num(row.deliverablesDone),
              reach: 0, views: own.views, impressions: 0, engagements: own.engagements, engagementRate: rate(own.engagements, own.views),
            };
          })
          .sort((a, b) => b.followers - a.followers),
      },
    };
  }
  if (method === 'POST' && /^\/campaigns\/[^/]+\/contracts\/[^/]+\/resend$/.test(p)) throw notSupported('Resending a contract');

  if (method === 'POST' && (m = p.match(/^\/campaigns\/([^/]+)\/creators\/([^/]+)\/send-brief$/))) {
    await call(`/campaigns/${m[1]}/creators/${m[2]}/brief`, 'POST', {});
    return { data: { id: m[2], stage: 'Hired' } };
  }
  if (method === 'POST' && (m = p.match(/^\/campaigns\/([^/]+)\/creators\/([^/]+)\/resend-invite$/))) {
    await call(p, 'POST', {});
    return { data: { id: m[2] } };
  }
  if (method === 'DELETE' && (m = p.match(/^\/campaigns\/([^/]+)\/creators\/([^/]+)$/))) {
    await call(p, 'DELETE');
    return { data: { id: m[2], stage: 'Declined' } };
  }

  // Roster controls. The agency API has add-creator but no CSV upload, so a file is sent one row at a time.
  if (method === 'POST' && (m = p.match(/^\/campaigns\/([^/]+)\/creators$/))) {
    await call(`/campaigns/${m[1]}/add-creator`, 'POST', { email: body.email, phone: body.phone || null, name: body.name || null });
    return { data: { added: 1, duplicates: [] } };
  }
  if (method === 'POST' && (m = p.match(/^\/campaigns\/([^/]+)\/creators\/bulk$/))) {
    const [header = '', ...lines] = str(body.csv).replace(/^\uFEFF/, '').split(/\r?\n/).filter((line) => line.trim());
    const columns = header.split(',').map((cell) => cell.trim().toLowerCase());
    let added = 0;
    const duplicates: string[] = [];
    for (const line of lines) {
      const cells = line.split(',').map((cell) => cell.trim());
      const at = (name: string) => (columns.indexOf(name) >= 0 ? cells[columns.indexOf(name)] ?? '' : '');
      const contact = { email: at('email') || null, phone: at('phone') || null, name: at('name') || null };
      if (!contact.email && !contact.phone) continue;
      try {
        await call(`/campaigns/${m[1]}/add-creator`, 'POST', contact);
        added++;
      } catch (error) {
        if (error instanceof ApiError && error.status === 409) duplicates.push(str(contact.email || contact.phone));
        else throw error;
      }
    }
    return { data: { added, duplicates } };
  }
  // Hire and respond work since twiva-server 722ce6b7d (BACKEND_GAPS #29, #30).
  if (method === 'POST' && (m = p.match(/^\/campaigns\/([^/]+)\/creators\/([^/]+)\/hire$/))) {
    await call(p, 'POST', {});
    return { data: { id: m[2], status: 'Active', statusKey: 'active' } };
  }
  if (method === 'POST' && (m = p.match(/^\/campaigns\/([^/]+)\/creators\/([^/]+)\/respond$/))) {
    const action = ({ accepted: 'accept', declined: 'decline', counterOffer: 'counter' } as Record<string, string>)[str(body.action)];
    await call(p, 'POST', { action, rate: body.proposedBudget ?? null, notes: body.message || null });
    return { data: { id: m[2], statusKey: { accept: 'accepted', decline: 'declined', counter: 'negotiating' }[action] ?? '' } };
  }
  // Payment tab: payable → processing in bulk (numeric ids), then paid one at a time.
  if (method === 'POST' && /^\/campaigns\/[^/]+\/bulk-mark-processing$/.test(p)) {
    const creatorIds = arr<string>(body.creatorIds).map((id) => Number(str(id).replace(/^cc-/, ''))).filter((id) => id > 0);
    return { data: { updated: num(((await call(p, 'POST', { creator_ids: creatorIds })).data as Json | undefined)?.updated) } };
  }
  if (method === 'POST' && (m = p.match(/^\/campaigns\/([^/]+)\/creators\/([^/]+)\/mark-paid$/))) {
    await call(p, 'POST', {});
    return { data: { id: m[2], statusKey: 'paid' } };
  }
  // No list of early payout requests in the agency API; the review is addressed by roster id.
  if (method === 'GET' && /^\/campaigns\/[^/]+\/early-payouts$/.test(p)) return { data: [] };
  if (method === 'POST' && (m = p.match(/^\/campaigns\/([^/]+)\/early-payouts\/[^/]+\/review$/))) {
    await call(`/campaigns/${m[1]}/creators/${str(body.rosterId)}/early-payout`, 'POST', {
      action: body.action === 'approved' ? 'approve' : 'reject', notes: body.reviewNotes || null,
    });
    return { data: null };
  }

  // Content review
  const refreshed = async (campaignId: string, contentId?: string) => {
    try {
      const all = await campaignDeliverables(campaignId);
      return contentId ? (all.find((item) => item.id === contentId) ?? { id: contentId }) : all;
    } catch {
      return contentId ? { id: contentId } : [];
    }
  };
  if (method === 'POST' && (m = p.match(/^\/campaigns\/([^/]+)\/contents\/([^/]+)\/review$/))) {
    if (body.decision === 'comment') throw notSupported('A note without a decision');
    try {
      await call(`/campaigns/${m[1]}/content/${m[2]}/review`, 'POST', { action: 'approve', note: body.text || undefined });
    } catch (error) {
      // Laravel approves first, then writes a reviewed_at column that doesn't exist (handoff #48):
      // the request fails although the content is approved. Trust the content's state.
      const after = (await refreshed(m[1], m[2])) as { status?: string };
      if (after.status !== 'Approved' && after.status !== 'Paid') throw error;
      return { data: after };
    }
    return { data: await refreshed(m[1], m[2]) };
  }
  if (method === 'POST' && (m = p.match(/^\/campaigns\/([^/]+)\/contents\/([^/]+)\/revision$/))) {
    await call(`/campaigns/${m[1]}/content/${m[2]}/revision`, 'POST', { feedback: body.text });
    return { data: await refreshed(m[1], m[2]) };
  }
  if (method === 'POST' && (m = p.match(/^\/campaigns\/([^/]+)\/contents\/bulk-approve$/))) {
    const reply = await call(`/campaigns/${m[1]}/content/bulk-approve`, 'POST', { content_ids: arr<string>(body.ids).map(Number) });
    return { data: { approved: num((reply.data as Json | undefined)?.approved), deliverables: await refreshed(m[1]) } };
  }

  // Creators and Discovery
  if (method === 'GET' && (m = p.match(/^\/creators\/([^/]+)\/campaigns$/))) {
    return { data: await creatorCampaigns(m[1], get) };
  }
  // The creator profile route (round 3, #42). Its phone, wallet id and balance are left out:
  // they aren't the agency's to see (reported to the backend).
  if (method === 'GET' && (m = p.match(/^\/creators\/([^/]+)$/))) {
    const reply = /^\d+$/.test(m[1]) ? await newRoute(() => get(`/creators/${m![1]}`)) : null;
    if (!reply) throw new ApiError(404, 'not_found', "Creator profiles aren't available from the production API yet.");
    const c = (reply.data ?? {}) as Json;
    const platforms = arr(c.platforms).map((account) => ({
      platform: str(account.platform), handle: str(account.handle), followers: num(account.followers),
      engagementRate: num(account.engagementRate), avgViews: 0, verified: false,
    }));
    return {
      data: {
        ...toCreatorProfile({ id: c.id, name: c.name, handle: c.username ? `@${str(c.username)}` : '', avatar: c.avatar,
          followers: c.totalFollowers, engagementRate: c.engagementRate, walletActive: c.walletActive }),
        markets: c.country ? [str(c.country)] : [],
        platforms,
        agencyHistory: { campaigns: arr(c.campaigns).length, onTimeRate: null, rating: null },
      },
    };
  }
  if (method === 'GET' && p === '/discovery') {
    // The agency API filters only by `q` and one `platform`; tiers, minimum engagement and
    // sort are applied here to its rows (it sends no categories or markets to filter on).
    const params = new URLSearchParams({ per_page: '100' });
    const q = url.searchParams.get('q');
    if (q) params.set('q', q);
    const platform = (url.searchParams.get('platforms') ?? '').split(',').filter(Boolean)[0];
    if (platform) params.set('platform', platform);
    const tiers = (url.searchParams.get('tiers') ?? '').split(',').filter(Boolean);
    const minEngagement = Number(url.searchParams.get('min_engagement') ?? 0);
    // Round 3 (#41) filters one tier and a minimum engagement on the server, across every
    // creator rather than the first 100; older servers ignore them and the filters below
    // still apply. Its market and category filters fail on the server, so aren't sent.
    if (tiers.length === 1) params.set('tier', tiers[0]);
    if (minEngagement > 0) params.set('min_engagement', String(minEngagement));
    const reply = await discoveryRows(`/discovery?${params}`, get);
    const sort = url.searchParams.get('sort');
    let data = arr(reply.data).map(toCreatorProfile);
    if (tiers.length) data = data.filter((c) => tiers.includes(tierOf(c.followers)));
    if (minEngagement > 0) data = data.filter((c) => c.engagementRate >= minEngagement);
    if (sort === 'engagement') data = [...data].sort((a, b) => b.engagementRate - a.engagementRate);
    const filtered = tiers.length > 0 || minEngagement > 0;
    const meta = (reply.meta ?? {}) as Json;
    return { data, meta: { ...meta, total: filtered ? data.length : num(meta.total), limit: 100 } };
  }
  if (method === 'GET' && p === '/discovery/options') {
    const d = ((await get(p)).data ?? {}) as Json;
    const names = (value: unknown) => arr<unknown>(value).map((item) => (typeof item === 'object' && item ? str((item as Json).name) : str(item)));
    return { data: { categories: names(d.categories), platforms: names(d.platforms), markets: [] } };
  }
  if (method === 'POST' && p === '/discovery/add-to-campaign') {
    try {
      await call(p, 'POST', { creator_id: Number(body.creatorId), campaign_id: Number(body.campaignId) });
      return { data: { added: 1, campaignId: str(body.campaignId) } };
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) return { data: { added: 0, campaignId: str(body.campaignId) } };
      throw error;
    }
  }

  // Saved lists (shortlists)
  const savedList = async (id: string) => {
    const d = ((await get(`/shortlists/${id}`)).data ?? {}) as Json;
    const creators = arr(d.creators);
    return {
      id: str(d.id), name: str(d.name), notes: str(d.description) || null,
      creatorIds: creators.map((c) => str(c.id)),
      members: creators.map((c) => ({ id: str(c.id), name: str(c.name) || 'Creator', avatar: (c.avatar as string | null) ?? null })),
      updatedAt: dateOnly(d.createdAt),
    };
  };
  if (method === 'GET' && p === '/shortlists') {
    const lists = arr((await get(p)).data);
    return list(await Promise.all(lists.map((item) => savedList(str(item.id)))));
  }
  if (method === 'POST' && p === '/shortlists') {
    const created = ((await call(p, 'POST', { name: body.name })).data ?? {}) as Json;
    for (const id of arr<string>(body.creatorIds)) {
      await call(`/shortlists/${str(created.id)}/creators`, 'POST', { creator_id: Number(id) }).catch(() => undefined);
    }
    return { data: await savedList(str(created.id)) };
  }
  // Editing a list; each reply is the refreshed lists, as our API sends. That API's notes field is `description`.
  const allLists = async () => Promise.all(arr((await get('/shortlists')).data).map((item) => savedList(str(item.id))));
  if (method === 'PATCH' && /^\/shortlists\/[^/]+$/.test(p)) {
    await call(p, 'PATCH', { name: body.name, description: body.notes || null });
    return { data: await allLists() };
  }
  if (method === 'DELETE' && /^\/shortlists\/[^/]+$/.test(p)) {
    await call(p, 'DELETE');
    return { data: await allLists() };
  }
  if (method === 'POST' && /^\/shortlists\/[^/]+\/creators$/.test(p)) {
    for (const id of arr<string>(body.creatorIds)) {
      // 409 = already on the list, which our API treats as done.
      await call(p, 'POST', { creator_id: Number(id) }).catch((error) => {
        if (!(error instanceof ApiError && error.status === 409)) throw error;
      });
    }
    return { data: await allLists() };
  }
  if (method === 'DELETE' && /^\/shortlists\/[^/]+\/creators\/[^/]+$/.test(p)) {
    await call(p, 'DELETE');
    return { data: await allLists() };
  }
  if (method === 'POST' && (m = p.match(/^\/shortlists\/([^/]+)\/add-to-campaign$/))) {
    const reply = await call(p, 'POST', { campaign_id: Number(body.campaignId) });
    return { data: { added: num((reply.data as Json | undefined)?.added), campaignId: str(body.campaignId) } };
  }

  // Wallets: the agency API has only the agency's own (funding) wallets.
  if (method === 'GET' && p === '/wallets') {
    if (url.searchParams.get('ownerType') === 'Creator') return list([]);
    return list(arr((await get('/wallets')).data).map(toFundingWallet));
  }
  // One wallet per agency (409 when it has one); without a name it is called "<agency> Wallet".
  if (method === 'POST' && p === '/wallets') return { data: toFundingWallet(((await call(p, 'POST', {})).data ?? {}) as Json) };
  if (method === 'GET' && /^\/wallets\/[^/]+$/.test(p)) return { data: toFundingWallet(((await get(p)).data ?? {}) as Json) };
  if (method === 'PATCH' && /^\/wallets\/[^/]+$/.test(p)) {
    return { data: toFundingWallet(((await call(p, 'PATCH', { name: body.name, description: body.description ?? null })).data ?? {}) as Json) };
  }
  if (method === 'POST' && /^\/wallets\/[^/]+\/(activate|deactivate)$/.test(p)) {
    return { data: toFundingWallet(((await call(p, 'POST')).data ?? {}) as Json) };
  }
  if (method === 'DELETE' && /^\/wallets\/[^/]+$/.test(p)) return call(p, 'DELETE');
  // Top-ups are made by Twiva admins (no agency endpoint) and show as ledger credits; the agency has one
  // ledger wallet, so its credits belong to it. The latest 100 lines are read.
  if (method === 'GET' && (m = p.match(/^\/wallets\/([^/]+)\/top-ups$/))) {
    const walletId = m[1];
    const rows = arr((await get('/payments/transactions?per_page=100&page=1')).data) as Json[];
    return list(rows.filter((t) => str(t.type) === 'credit' && !isLedgerRefund(str(t.description))).map((t) => toLedgerTopUp(t, walletId)));
  }
  // No funding instructions: never show the screens' sample bank accounts as real ones.
  if (/^\/wallets\/[^/]+\/top-ups$/.test(p) || p.startsWith('/funding/methods') || p.startsWith('/funding/bank-accounts')) {
    throw notSupported('Funding a wallet');
  }

  // Whose wallet an ID is (Single Payment's name check).
  if (method === 'POST' && p === '/payments/resolve') {
    const info = ((await call(p, 'POST', { wallet_id: Number(body.walletId) })).data ?? {}) as Json;
    return { data: { exists: Boolean(info.exists), type: str(info.type), name: (info.name as string | null) ?? null } };
  }
  // Single payments: wallet-to-wallet only, through payments → payments/otp; no payee directory or payment list.
  if (method === 'GET' && (p === '/payees' || p === '/payments')) return list([]);
  if (method === 'GET' && p === '/payees/wallet-holders') return list(await searchWalletHolders(get, url.searchParams.get('q') ?? ''));
  if (method === 'POST' && p === '/payments/otp') {
    if (body.channel !== 'wallet') throw notSupported(`Paying by ${SINGLE_CHANNELS[str(body.channel)] ?? 'this channel'}`);
    const destination = (body.destination ?? {}) as Json;
    const reply = ((await call('/payments', 'POST', {
      from_wallet_id: Number(body.sourceWalletId),
      to_wallet_id: Number(destination.walletId),
      amount: num(body.amount),
      description: str(body.description) || 'Creator payment',
      // From a campaign's Pay button: the backend marks a payable creator paid (BACKEND_GAPS #63).
      ...(body.campaignCreatorId ? { campaign_creator_id: Number(str(body.campaignCreatorId).replace(/^cc-/, '')) } : {}),
    })).data ?? {}) as Json;
    // Without an OTP the call has already paid.
    return { data: reply.requiresOtp ? { requiresOtp: true } : { requiresOtp: false, payment: toSinglePayment(body) } };
  }
  if (method === 'POST' && p === '/payments') {
    await call('/payments/otp', 'POST', { otp: body.otp });
    return { data: toSinglePayment(body) };
  }
  // Abandoning the OTP: the backend refunds the ledger debit it took when sending the code.
  if (method === 'POST' && p === '/payments/otp/cancel') {
    await call('/payments/cancel', 'POST', {});
    return { data: { cancelled: true } };
  }

  // Payouts: the agency API lists only batch summaries (`/payouts`), never the creator payouts the screen shows.
  // Payout upload: that API takes rows of { wallet_id, amount, description } plus the ledger wallet
  // to pay from, checks the wallet id, amount and balance, then confirm debits the ledger and pays
  // them, usually behind an OTP (payouts/verify-otp).
  if (method === 'POST' && p === '/payouts/validate') {
    const lines = str(body.csv).replace(/^\uFEFF/, '').split(/\r?\n/).filter((line) => line.trim()).slice(1);
    const today = new Date().toISOString().slice(0, 10);
    const parsed = lines.map((line) => line.split(',').map((cell) => cell.trim())).filter((cells) => cells.length >= 4)
      .map((cells) => ({
        walletId: cells[0] ?? '', creatorName: cells[1] ?? '', campaignName: cells[2] ?? '', amount: num(cells[3]),
        payoutDate: cells[4] || today, notes: cells[5] ?? '',
      }));
    const reply = ((await call(p, 'POST', {
      rows: parsed.map((row) => ({ wallet_id: row.walletId, amount: row.amount, description: row.notes || row.campaignName || 'Payout' })),
      from_ledger_wallet_id: Number(body.walletId),
    })).data ?? {}) as Json;
    const failed = new Map(arr(reply.errors).map((error) => [num(error.row) - 1, str(error.message)]));
    const rows = parsed.map((row, index) => {
      const errors = failed.has(index) ? [failed.get(index) as string] : [];
      return { ...row, valid: errors.length === 0, errors, status: errors.length === 0 ? 'Valid' : 'Invalid' };
    });
    // A backend without the #57 fix answers no `sufficient`: it would pay from Twiva's pool unchecked.
    const checked = typeof reply.sufficient === 'boolean';
    return {
      data: {
        rows, validCount: num(reply.valid), invalidCount: rows.length - num(reply.valid), totalAmount: num(reply.totalAmount),
        availableBalance: checked ? num(reply.availableBalance) : null, sufficient: checked ? reply.sufficient : false,
      },
    };
  }
  if (method === 'POST' && p === '/payouts/confirm') {
    const reply = ((await call(p, 'POST', {})).data ?? {}) as Json;
    return { data: { requiresOtp: Boolean(reply.requiresOtp), reference: str(reply.reference), total: num(reply.total), count: num(reply.count) } };
  }
  if (method === 'POST' && p === '/payouts/verify-otp') {
    return { data: ((await call(p, 'POST', { otp: body.otp })).data ?? {}) as Json };
  }
  if (method === 'POST' && p === '/payouts/cancel') {
    await call(p, 'POST', {});
    return { data: { cancelled: true } };
  }
  if (method === 'GET' && p === '/payouts') return list([]);
  if (method === 'GET' && /^\/payouts\/[^/]+$/.test(p)) throw new ApiError(404, 'not_found', 'Payout not found.');
  if (method === 'POST' && /^\/payouts\/[^/]+\/retry$/.test(p)) throw notSupported('Retrying a payout');
  if (method === 'GET' && p === '/rails/status') return { data: [] };

  // Transactions: the agency API's payment history, a page at a time (100 at most).
  if (method === 'GET' && p === '/transactions') {
    const page = url.searchParams.get('page') ?? '1';
    const reply = await get(`/payments/transactions?per_page=100&page=${encodeURIComponent(page)}`);
    return { data: arr(reply.data).map(toTransaction), meta: reply.meta };
  }
  if (p.startsWith('/transactions/')) throw notSupported('Reconciling transactions');

  // Bulk payments: campaign creators marked payable, paid through bulk-payments/preview → confirm → otp.
  if (method === 'GET' && p === '/bulk-transfers') return list([]);
  if (method === 'GET' && p === '/bulk-transfers/candidates') {
    return list(await bulkCandidates(get, call, url.searchParams.get('sourceWalletId') ?? '', url.searchParams.get('currency') ?? ''));
  }
  if (method === 'POST' && p === '/bulk-transfers/validate') throw notSupported('Reading a payment file');
  if (method === 'POST' && p === '/bulk-transfers/otp') {
    if (body.channel === 'mobile') throw notSupported('Paying by Mobile Money in bulk');
    if ((body.schedule as Json | undefined)?.mode === 'scheduled') throw notSupported('Scheduling a bulk payment');
    // Confirm rebuilds the batch on the server (BACKEND_GAPS #35): it pays the creators named in
    // creator_ids at their agreed fee, and pays every payable creator in the agency when none are
    // named. So always name them, and refuse amounts the server won't use.
    const preview = ((await call('/bulk-payments/preview', 'POST', { from_wallet_id: Number(body.sourceWalletId) })).data ?? {}) as Json;
    const previewed = new Map(arr(preview.transactions).map((t) => [`cc-${str(t.ccId)}`, t]));
    const recipients = arr(body.recipients);
    if (recipients.length === 0) throw new ApiError(422, 'invalid_state', 'Add at least one creator to the batch.');
    const missing = recipients.filter((row) => !previewed.has(str(row.id)));
    if (missing.length > 0) {
      throw new ApiError(409, 'invalid_state', `${missing.map((row) => str(row.name) || str(row.id)).join(', ')} can no longer be paid from this wallet. Reload and try again.`);
    }
    const changed = recipients.filter((row) => num(row.amount) !== num((previewed.get(str(row.id)) as Json).amount));
    if (changed.length > 0) {
      throw new ApiError(
        409,
        'invalid_state',
        `The backend pays each creator their agreed fee, so amounts can't be changed here: ${changed
          .map((row) => `${str(row.name) || str(row.id)} (${num((previewed.get(str(row.id)) as Json).amount)})`)
          .join(', ')}. Set those amounts back and try again.`,
      );
    }
    const creatorIds = recipients.map((row) => Number(str(row.id).replace(/^cc-/, '')));
    if (creatorIds.some((id) => !Number.isInteger(id) || id <= 0)) {
      throw new ApiError(422, 'invalid_state', "Some creators in the batch couldn't be matched. Reload and try again.");
    }
    const reply = ((await call('/bulk-payments/confirm', 'POST', {
      from_ledger_wallet_id: num(preview.fromLedgerWalletId) || Number(body.sourceWalletId),
      creator_ids: creatorIds,
    })).data ?? {}) as Json;
    // Without an OTP the confirm call has already paid the batch.
    return { data: reply.requiresOtp ? { requiresOtp: true } : { requiresOtp: false, transfer: toBulkTransfer(body) } };
  }
  if (method === 'POST' && p === '/bulk-transfers/otp/cancel') {
    await call('/bulk-payments/cancel', 'POST', {});
    return { data: { cancelled: true } };
  }
  if (method === 'POST' && p === '/bulk-transfers') {
    await call('/bulk-payments/otp', 'POST', { otp: body.otp });
    return { data: toBulkTransfer(body) };
  }
  if (/^\/bulk-transfers\/[^/]+\/(cancel|schedule)$/.test(p)) throw notSupported('Changing a scheduled bulk payment');
  if (method === 'GET' && p === '/recipient-lists') return list([]);
  if (p.startsWith('/recipient-lists')) throw notSupported('Saving recipient lists');
  if (method === 'GET' && p === '/funding/pool-account') return { data: null };

  // Invoices
  if (method === 'GET' && p === '/invoices') {
    return list(arr((await get(withPerPage(path))).data).map(toInvoice));
  }
  if (method === 'GET' && /^\/invoices\/[^/]+$/.test(p)) return { data: toInvoice(((await get(p)).data ?? {}) as Json) };
  if (method === 'POST' && (m = p.match(/^\/invoices\/([^/]+)\/(approve|reject)$/))) {
    await call(p, 'POST', m[2] === 'reject' ? { reason: body.note } : {});
    return { data: toInvoice(((await get(`/invoices/${m[1]}`)).data ?? {}) as Json) };
  }
  if (method === 'POST' && /^\/invoices\/[^/]+\/hold$/.test(p)) throw notSupported('Putting an invoice on hold');
  if (method === 'POST' && p === '/invoices/bulk-action') {
    const ids = arr<string>(body.ids);
    const reply = await call(p, 'POST', { ids, action: body.action, reason: body.note });
    const updated = num((reply.data as Json | undefined)?.updated);
    return { data: { changed: updated, skipped: Math.max(0, ids.length - updated) } };
  }

  // Reports (the agency API reports all time; the period isn't sent)
  if (method === 'GET' && p === '/reports') {
    const d = ((await get('/reports')).data ?? {}) as Json;
    return {
      data: {
        campaigns: arr(d.campaignBreakdown).map((c) => ({
          id: str(c.id), name: str(c.name), brandClient: '', market: '', markets: [], currency: 'KES', budget: 0,
          spend: num(c.spend), creators: num(c.creatorCount), delivered: 0, totalDeliverables: 0, reach: 0,
          engagements: 0, engagementRate: 0, status: LIFECYCLE[str(c.status)] ?? str(c.status),
        })),
        creators: [],
      },
    };
  }

  // Settings
  if (p === '/settings/branding/public') return { data: { agency: null } };
  if (p === '/settings/branding/reset') throw notSupported('Resetting branding');
  if (p === '/settings/branding/verify-domain') throw notSupported('Verifying a domain');
  if (p === '/settings/branding') {
    if (method === 'PUT') {
      const profile = (body.profile ?? {}) as Json;
      const colours = (body.colours ?? {}) as Json;
      await call(p, 'PATCH', {
        name: profile.name, tagline: profile.tagline || null, website: profile.website || null,
        primary_color: colours.primary ?? null, accent_color: colours.accent ?? null, secondary_color: colours.secondary ?? null,
        // Saved since round 3 (#13); older servers ignore it.
        ...(Array.isArray(profile.markets) ? { markets: profile.markets } : {}),
      });
    }
    const [branding, agency] = await Promise.all([get(p), get('/profile').catch(() => ({ data: {} }) as Body)]);
    return { data: toBranding((branding.data ?? {}) as Json, (agency.data ?? {}) as Json) };
  }
  if (method === 'GET' && p === '/settings/audit') {
    return { data: arr((await get(withPerPage('/audit'))).data).map(toAuditEvent) };
  }

  if (p.startsWith('/dev/outbox')) throw new ApiError(404, 'not_found', 'The outbox only exists on the local API.');

  // Same path, verb and shape on both APIs: /me, staff, roles, …
  return call(path, method, init.body ? body : undefined);
}
